// src/components/Analytics/PayrollAnalytics.js
import React, { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { auth, db } from "../../firebase/config";
import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  setDoc,
} from "firebase/firestore";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

import "../../styles/Analytics.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const money = (n) =>
  Number(n || 0).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });

/**
 * ✅ Drilldown support:
 * - If employeeId is provided, we only show employee-specific analytics.
 */
export default function PayrollAnalytics({ employeeId = null }) {
  const isDrilldown = !!employeeId;

  const [companyId, setCompanyId] = useState(null);
  const [companyTimeZone, setCompanyTimeZone] = useState("UTC");
  const [payrollConfig, setPayrollConfig] = useState(null);
  const [employees, setEmployees] = useState({}); // id -> data

  const [startDate, setStartDate] = useState(""); // YYYY-MM-DD
  const [endDate, setEndDate] = useState(""); // YYYY-MM-DD
  const [userId, setUserId] = useState(null);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ─────────────────────────────────────────────
  // Bootstrap: user → company → payrollConfig + employees + restore filters
  // ─────────────────────────────────────────────
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setError("Employer not authenticated.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");
        setUserId(user.uid);

        // 1) users/{uid}
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        const userData = userSnap.data() || {};
        const cid = userData.companyId;

        if (!cid) {
          setError("Company not found for this user.");
          setLoading(false);
          return;
        }
        setCompanyId(cid);

        // 2) companies/{cid}
        const companyRef = doc(db, "companies", cid);
        const companySnap = await getDoc(companyRef);
        const companyData = companySnap.data() || {};
        const tz = companyData.timeZone || "UTC";
        setCompanyTimeZone(tz);
        setPayrollConfig(companyData.payrollConfig || null);

        // 3) Employees map
        const empRef = collection(db, `companies/${cid}/employees`);
        const empSnap = await getDocs(empRef);
        const map = {};
        empSnap.forEach((d) => {
          map[d.id] = { id: d.id, ...d.data() };
        });
        setEmployees(map);

        // 4) Restore date filters (same behavior)
        const savedFilters = userData.payrollAnalyticsFilters;
        if (
          savedFilters &&
          savedFilters.companyId === cid &&
          savedFilters.startDate &&
          savedFilters.endDate
        ) {
          setStartDate(savedFilters.startDate);
          setEndDate(savedFilters.endDate);
        } else {
          const today = DateTime.now()
            .setZone(tz)
            .startOf("day");
          const start = today.minus({ days: 29 });
          setStartDate(start.toISODate());
          setEndDate(today.toISODate());
        }

        setLoading(false);
      } catch (e) {
        console.error("PayrollAnalytics bootstrap error", e);
        setError("Unable to load payroll analytics.");
        setLoading(false);
      }
    });

    return () => unsub();
  }, []);

  // ✅ Save date filters
  useEffect(() => {
    const saveFilters = async () => {
      if (!userId || !companyId || !startDate || !endDate) return;

      try {
        const userRef = doc(db, "users", userId);
        await setDoc(
          userRef,
          {
            payrollAnalyticsFilters: { companyId, startDate, endDate },
          },
          { merge: true }
        );
      } catch (e) {
        console.error("Failed to save payroll analytics filters", e);
      }
    };

    saveFilters();
  }, [userId, companyId, startDate, endDate]);

  // Friendly label
  const rangeLabel = useMemo(() => {
    if (!startDate || !endDate) return "";
    try {
      const tz = companyTimeZone || "UTC";
      const start = DateTime.fromISO(startDate, { zone: tz }).startOf("day");
      const end = DateTime.fromISO(endDate, { zone: tz }).startOf("day");
      if (!start.isValid || !end.isValid) return "";

      const sameMonth = start.month === end.month && start.year === end.year;
      return sameMonth
        ? `${start.toFormat("MMM dd")} – ${end.toFormat("dd, yyyy")}`
        : `${start.toFormat("MMM dd, yyyy")} – ${end.toFormat("MMM dd, yyyy")}`;
    } catch {
      return "";
    }
  }, [startDate, endDate, companyTimeZone]);

  // ─────────────────────────────────────────────
  // Load payroll-style data for current range (company-wide or drilldown)
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!companyId || !startDate || !endDate) return;

    if (startDate > endDate) {
      setError("Start date cannot be after end date.");
      setRows([]);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const baseRef = collection(db, `companies/${companyId}/time_tracking`);

        // ✅ Drilldown: add where("userId","==",employeeId)
        const qRef = employeeId
          ? query(
              baseRef,
              where("userId", "==", employeeId),
              where("date", ">=", startDate),
              where("date", "<=", endDate)
            )
          : query(
              baseRef,
              where("date", ">=", startDate),
              where("date", "<=", endDate)
            );

        const snap = await getDocs(qRef);

        // userId -> aggregation
        const agg = {};

        snap.forEach((docSnap) => {
          const d = docSnap.data();
          const id = d.userId;
          if (!id) return;

          const hrs = Number(d.hoursWorked || 0);
          const date = d.date; // YYYY-MM-DD (company TZ)
          const entryType = d.entryType || "";
          const status = d.status || "";

          const isOnLeaveStatus = status === "On Leave";
          const isPaidLeave =
            entryType === "paid-leave" ||
            d.leavePaid === true ||
            d.isPaidLeave === true;
          const isAnyLeave =
            isPaidLeave || entryType === "unpaid-leave" || isOnLeaveStatus;
          const isUnpaidLeave = isAnyLeave && !isPaidLeave;

          if (!agg[id]) {
            agg[id] = {
              entries: 0,
              totalHours: 0,
              workHours: 0,
              paidLeaveHours: 0,
              unpaidLeaveHours: 0,
              perDayWork: {}, // date -> work hours
              perDayAll: {}, // date -> all payable hours (work + paid leave)
            };
          }

          agg[id].entries += 1;
          agg[id].totalHours += hrs;

          // track daily totals (for drilldown chart)
          if (date) {
            agg[id].perDayAll[date] = (agg[id].perDayAll[date] || 0) + hrs;
          }

          if (isPaidLeave) {
            agg[id].paidLeaveHours += hrs;
          } else if (isUnpaidLeave) {
            agg[id].unpaidLeaveHours += hrs;
          } else {
            agg[id].workHours += hrs;
            if (date) {
              agg[id].perDayWork[date] = (agg[id].perDayWork[date] || 0) + hrs;
            }
          }
        });

        // Overtime config
        const config = payrollConfig || {};
        const overtimeOn = config.overtimeEnabled ?? false;
        const overtimeBasis = config.overtimeBasis || "weekly";
        const weeklyThreshold = Number(
          config.overtimeWeeklyHours ?? config.overtimeHours ?? 40
        );
        const dailyThreshold = Number(config.overtimeDailyHours ?? 8);
        const overtimeMultiplier = Number(config.overtimeMultiplier ?? 1.5);

        const tz = companyTimeZone || "UTC";
        const anchorISO =
          config.anchorDate || startDate || DateTime.now().toISODate();
        let workweekAnchor = DateTime.fromISO(anchorISO, { zone: tz }).startOf(
          "day"
        );
        if (!workweekAnchor.isValid) {
          workweekAnchor = DateTime.fromISO(startDate, { zone: tz }).startOf(
            "day"
          );
        }

        // Build rows
        const rowList = Object.entries(agg).map(([uid, stats]) => {
          const emp = employees[uid] || {};
          const name =
            emp.name || emp.fullName || emp.displayName || "Unknown employee";
          const jobTitle = emp.jobTitle || emp.position || "—";
          const department =
            emp.department || emp.group || emp.departmentName || "—";

          const rateRaw =
            emp.hourlyRate ??
            emp.hourly_rate ??
            emp.payRate ??
            emp.pay_rate ??
            0;
          const rate = Number(rateRaw || 0);

          const workedHours = Number((stats.workHours || 0).toFixed(2));
          const paidLeaveHours = Number((stats.paidLeaveHours || 0).toFixed(2));
          const unpaidLeaveHours = Number(
            (stats.unpaidLeaveHours || 0).toFixed(2)
          );

          // Payable = work + paid leave
          const totalHours = Number((workedHours + paidLeaveHours).toFixed(2));

          let regularHours = workedHours;
          let overtimeHours = 0;

          // OT is from worked hours only (never paid leave)
          if (overtimeOn && rate > 0 && workedHours > 0) {
            if (overtimeBasis === "weekly") {
              const threshold = weeklyThreshold > 0 ? weeklyThreshold : 0;
              const weeks = {};

              Object.entries(stats.perDayWork || {}).forEach(
                ([dateStr, dayHours]) => {
                  try {
                    const day = DateTime.fromISO(dateStr, { zone: tz }).startOf(
                      "day"
                    );
                    if (!day.isValid) return;

                    const diffDays = Math.floor(
                      day.diff(workweekAnchor, "days").days
                    );
                    const weekIndex = Math.floor(diffDays / 7);
                    const weekStart = workweekAnchor
                      .plus({ days: weekIndex * 7 })
                      .toISODate();

                    weeks[weekStart] =
                      (weeks[weekStart] || 0) + Number(dayHours);
                  } catch {}
                }
              );

              let reg = 0;
              let ot = 0;
              Object.values(weeks).forEach((weekHours) => {
                reg += Math.min(weekHours, threshold);
                ot += Math.max(0, weekHours - threshold);
              });

              regularHours = Number(reg.toFixed(2));
              overtimeHours = Number(ot.toFixed(2));
            } else {
              const threshold = dailyThreshold > 0 ? dailyThreshold : 0;
              let reg = 0;
              let ot = 0;
              Object.values(stats.perDayWork || {}).forEach((h) => {
                reg += Math.min(h, threshold);
                ot += Math.max(0, h - threshold);
              });
              regularHours = Number(reg.toFixed(2));
              overtimeHours = Number(ot.toFixed(2));
            }
          }

          const regularPay = regularHours * rate;
          const overtimePay = overtimeHours * rate * overtimeMultiplier;
          const paidLeavePay = paidLeaveHours * rate;
          const grossPay = Number(
            (regularPay + overtimePay + paidLeavePay).toFixed(2)
          );

          return {
            userId: uid,
            name,
            jobTitle,
            department,
            entries: stats.entries,
            workedHours,
            paidLeaveHours,
            unpaidLeaveHours,
            totalHours,
            regularHours,
            overtimeHours,
            hourlyRate: rate,
            regularPay,
            overtimePay,
            paidLeavePay,
            grossPay,
            perDayAll: stats.perDayAll || {},
          };
        });

        rowList.sort((a, b) => a.name.localeCompare(b.name));
        setRows(rowList);
        setLoading(false);
      } catch (e) {
        console.error("PayrollAnalytics load error", e);
        setError("Failed to load payroll analytics.");
        setRows([]);
        setLoading(false);
      }
    };

    load();
  }, [
    companyId,
    startDate,
    endDate,
    companyTimeZone,
    payrollConfig,
    employees,
    employeeId,
  ]);

  // ─────────────────────────────────────────────
  // Derived views
  // ─────────────────────────────────────────────

  // ✅ If drilldown: the single employee row (or null)
  const employeeRow = useMemo(() => {
    if (!isDrilldown) return null;
    return rows.find((r) => r.userId === employeeId) || null;
  }, [rows, isDrilldown, employeeId]);

  // Company totals (only meaningful when NOT drilldown)
  const totals = useMemo(() => {
    if (!rows.length)
      return {
        employees: 0,
        payableHours: 0,
        regular: 0,
        overtime: 0,
        gross: 0,
        paidLeave: 0,
        unpaidLeave: 0,
        regularPay: 0,
        overtimePay: 0,
        paidLeavePay: 0,
      };

    const employeesCount = rows.length;
    const payableHours = rows.reduce((sum, r) => sum + (r.totalHours || 0), 0);
    const regular = rows.reduce((sum, r) => sum + (r.regularHours || 0), 0);
    const overtime = rows.reduce((sum, r) => sum + (r.overtimeHours || 0), 0);
    const gross = rows.reduce((sum, r) => sum + (r.grossPay || 0), 0);
    const paidLeave = rows.reduce((sum, r) => sum + (r.paidLeaveHours || 0), 0);
    const unpaidLeave = rows.reduce(
      (sum, r) => sum + (r.unpaidLeaveHours || 0),
      0
    );
    const regularPay = rows.reduce((sum, r) => sum + (r.regularPay || 0), 0);
    const overtimePay = rows.reduce((sum, r) => sum + (r.overtimePay || 0), 0);
    const paidLeavePay = rows.reduce(
      (sum, r) => sum + (r.paidLeavePay || 0),
      0
    );

    return {
      employees: employeesCount,
      payableHours: Number(payableHours.toFixed(2)),
      regular: Number(regular.toFixed(2)),
      overtime: Number(overtime.toFixed(2)),
      gross: Number(gross.toFixed(2)),
      paidLeave: Number(paidLeave.toFixed(2)),
      unpaidLeave: Number(unpaidLeave.toFixed(2)),
      regularPay,
      overtimePay,
      paidLeavePay,
    };
  }, [rows]);

  // ✅ Employee KPIs (only in drilldown)
  const employeeKPIs = useMemo(() => {
    if (!employeeRow) {
      return {
        totalHours: 0,
        regularHours: 0,
        overtimeHours: 0,
        grossPay: 0,
        paidLeaveHours: 0,
        unpaidLeaveHours: 0,
        avgHourlyCost: 0,
      };
    }
    const totalHours = employeeRow.totalHours || 0;
    const grossPay = employeeRow.grossPay || 0;

    return {
      totalHours,
      regularHours: employeeRow.regularHours || 0,
      overtimeHours: employeeRow.overtimeHours || 0,
      grossPay,
      paidLeaveHours: employeeRow.paidLeaveHours || 0,
      unpaidLeaveHours: employeeRow.unpaidLeaveHours || 0,
      avgHourlyCost: totalHours
        ? Number((grossPay / totalHours).toFixed(2))
        : 0,
    };
  }, [employeeRow]);

  // ✅ Drilldown chart: daily hours (payable hours = work + paid leave)
  const dailyHoursChart = useMemo(() => {
    if (!employeeRow) return null;

    const perDay = employeeRow.perDayAll || {};
    const days = Object.keys(perDay).sort(); // YYYY-MM-DD sorted

    if (!days.length) return null;

    return {
      labels: days.map((d) =>
        DateTime.fromISO(d, { zone: companyTimeZone }).toFormat("MMM dd")
      ),
      datasets: [
        {
          label: "Payable hours (Work + Paid Leave)",
          data: days.map((d) => Number(perDay[d] || 0)),
        },
      ],
    };
  }, [employeeRow, companyTimeZone]);

  // Company charts (only show when NOT drilldown)
  const grossChartData = useMemo(() => {
    if (!rows.length) return null;
    return {
      labels: rows.map((r) => r.name),
      datasets: [{ label: "Gross pay", data: rows.map((r) => r.grossPay) }],
    };
  }, [rows]);

  const overtimeChartData = useMemo(() => {
    const withOT = rows.filter((r) => r.overtimeHours > 0);
    if (!withOT.length) return null;
    return {
      labels: withOT.map((r) => r.name),
      datasets: [
        { label: "Overtime hours", data: withOT.map((r) => r.overtimeHours) },
      ],
    };
  }, [rows]);

  // ─────────────────────────────────────────────
  // UI guards
  // ─────────────────────────────────────────────
  if (loading && !rows.length) {
    return <p className="analytics-loading">Loading payroll analytics…</p>;
  }

  return (
    <div className="management-analytics-wrapper">
      <h1>Payroll Analytics</h1>

      {/* Drilldown hint */}
      <div className="analytics-hint">
        Showing payroll analytics for{" "}
        <b>{isDrilldown ? "1 employee" : "all employees"}</b>.
      </div>

      {/* Header row */}
      <div className="analytics-header-row">
        <div className="analytics-header-left">
          <p className="analytics-subtitle">
            {isDrilldown
              ? "Review hours, overtime, leave, and estimated gross pay for this employee."
              : "Understand total hours, overtime, and estimated payroll cost for the selected date range."}
          </p>
          {rangeLabel && (
            <p className="analytics-range-label">Current range: {rangeLabel}</p>
          )}
        </div>
      </div>

      {/* Date filters */}
      <div className="analytics-date-filters">
        <div className="analytics-field">
          <label>From</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="analytics-field">
          <label>To</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
      </div>

      {error && <p className="analytics-error">{error}</p>}

      {/* ─────────────────────────── Drilldown: employee-only view ─────────────────────────── */}
      {isDrilldown ? (
        <>
          {!employeeRow && !loading ? (
            <p className="analytics-empty">
              No time tracking entries found for this employee in this date
              range.
            </p>
          ) : (
            <>
              {/* Optional: employee header card */}
              {employeeRow && (
                <div className="summary-card" style={{ marginBottom: 14 }}>
                  <div className="summary-label">Employee</div>
                  <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontWeight: 700 }}>{employeeRow.name}</div>
                      <div style={{ color: "#64748b", fontSize: 13 }}>
                        {employeeRow.jobTitle} • {employeeRow.department}
                      </div>
                    </div>
                    <div style={{ marginLeft: "auto" }}>
                      <div style={{ color: "#64748b", fontSize: 12 }}>
                        Hourly rate
                      </div>
                      <div style={{ fontWeight: 700 }}>
                        {money(employeeRow.hourlyRate)}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Employee KPIs only */}
              <div className="kpi-grid">
                <div className="kpi-card accent-green">
                  <div className="kpi-top">
                    <span className="kpi-dot" />
                    <span className="kpi-label">Total payable hours</span>
                  </div>
                  <div className="kpi-value">
                    {employeeKPIs.totalHours.toFixed(2)}
                  </div>
                  <div className="kpi-sub">Work + paid leave</div>
                </div>

                <div className="kpi-card accent-amber">
                  <div className="kpi-top">
                    <span className="kpi-dot" />
                    <span className="kpi-label">Regular vs OT hours</span>
                  </div>
                  <div className="kpi-value">
                    {employeeKPIs.regularHours.toFixed(1)} /{" "}
                    {employeeKPIs.overtimeHours.toFixed(1)}
                  </div>
                  <div className="kpi-sub">OT from worked hours only</div>
                </div>

                <div className="kpi-card accent-purple">
                  <div className="kpi-top">
                    <span className="kpi-dot" />
                    <span className="kpi-label">Estimated gross pay</span>
                  </div>
                  <div className="kpi-value">
                    {money(employeeKPIs.grossPay)}
                  </div>
                  <div className="kpi-sub">Regular + OT + paid leave</div>
                </div>

                <div className="kpi-card accent-teal">
                  <div className="kpi-top">
                    <span className="kpi-dot" />
                    <span className="kpi-label">Avg hourly payroll cost</span>
                  </div>
                  <div className="kpi-value">
                    {money(employeeKPIs.avgHourlyCost)}
                  </div>
                  <div className="kpi-sub">Gross / payable hours</div>
                </div>

                <div className="kpi-card accent-gray">
                  <div className="kpi-top">
                    <span className="kpi-dot" />
                    <span className="kpi-label">
                      Leave hours (paid / unpaid)
                    </span>
                  </div>
                  <div className="kpi-value">
                    {employeeKPIs.paidLeaveHours.toFixed(1)} /{" "}
                    {employeeKPIs.unpaidLeaveHours.toFixed(1)}
                  </div>
                  <div className="kpi-sub">Leave time in this range</div>
                </div>
              </div>

              {/* Employee charts only */}
              {dailyHoursChart && (
                <div className="charts-grid" style={{ marginTop: 14 }}>
                  <div className="chart-container">
                    <h3>Daily payable hours</h3>
                    <Bar
                      data={dailyHoursChart}
                      options={{
                        responsive: true,
                        plugins: { legend: { position: "top" } },
                      }}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        /* ─────────────────────────── Company-wide view (unchanged) ─────────────────────────── */
        <>
          {/* KPI cards (company-wide) */}
          <div className="kpi-grid">
            <div className="kpi-card accent-blue">
              <div className="kpi-top">
                <span className="kpi-dot" />
                <span className="kpi-label">Employees with hours</span>
              </div>
              <div className="kpi-value">{totals.employees}</div>
              <div className="kpi-sub">
                Employees who have entries in this range.
              </div>
            </div>

            <div className="kpi-card accent-green">
              <div className="kpi-top">
                <span className="kpi-dot" />
                <span className="kpi-label">Total payable hours</span>
              </div>
              <div className="kpi-value">{totals.payableHours.toFixed(2)}</div>
              <div className="kpi-sub">
                Worked hours + paid leave hours for this period.
              </div>
            </div>

            <div className="kpi-card accent-amber">
              <div className="kpi-top">
                <span className="kpi-dot" />
                <span className="kpi-label">Regular vs OT hours</span>
              </div>
              <div className="kpi-value">
                {totals.regular.toFixed(1)} / {totals.overtime.toFixed(1)}
              </div>
              <div className="kpi-sub">From worked time only.</div>
            </div>

            <div className="kpi-card accent-purple">
              <div className="kpi-top">
                <span className="kpi-dot" />
                <span className="kpi-label">Estimated gross pay</span>
              </div>
              <div className="kpi-value">{money(totals.gross)}</div>
              <div className="kpi-sub">Regular + OT + paid leave.</div>
            </div>

            <div className="kpi-card accent-gray">
              <div className="kpi-top">
                <span className="kpi-dot" />
                <span className="kpi-label">Leave hours (paid / unpaid)</span>
              </div>
              <div className="kpi-value">
                {totals.paidLeave.toFixed(1)} / {totals.unpaidLeave.toFixed(1)}
              </div>
              <div className="kpi-sub">Company totals in this range.</div>
            </div>
          </div>

          {/* Charts */}
          {rows.length > 0 && (
            <div className="charts-grid">
              {grossChartData && (
                <div className="chart-container">
                  <h3>Gross pay by employee</h3>
                  <Bar
                    data={grossChartData}
                    options={{
                      responsive: true,
                      plugins: { legend: { position: "top" } },
                    }}
                  />
                </div>
              )}

              {overtimeChartData && (
                <div className="chart-container">
                  <h3>Overtime hours by employee</h3>
                  <Bar
                    data={overtimeChartData}
                    options={{
                      responsive: true,
                      plugins: { legend: { position: "top" } },
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {!rows.length && !loading && (
            <p className="analytics-empty">
              No time tracking entries found for this date range.
            </p>
          )}
        </>
      )}
    </div>
  );
}
